import { useCallback, useEffect, useState } from "react";
import { useQueries } from "react-query";
import qs from "qs";
import { apiConfig } from "../config/apiConfig";
import apiService from "../service/apiService";
import { useCookie } from "./useCookie";
import { removeEmptyFilterQueries, toQuery } from "../helpers/general";
import { useAuth } from "./useAuth";
import { useSelectedPlace } from "./place/useSelectedPlace";

const STORAGE_KEY = "vz_seen_newest";

type SeenMap = Record<string, string>;

const readSeen = (): SeenMap => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");
  } catch {
    return {};
  }
};

export function useNewDataAlerts(enabled: boolean) {
  const { token } = useCookie("vToken");
  const { user, isAdmin } = useAuth();
  const [seen, setSeen] = useState<SeenMap>(readSeen);
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  // Newest item of a list, assuming the endpoint supports sort by createdAt desc
  const newest = (endpoint: string) => async () => {
    const res = await apiService.MakeGetRequest(endpoint, token);
    return (res?.items?.[0]?.createdAt as string | undefined) ?? null;
  };

  // One entry per sidebar path. Adapt the endpoints to your apiConfig.
  const driversEndpoint = (status?: string) =>
    `users/drivers${toQuery({
      page: 1,
      limit: 1,
      query: "",
      status: status ?? "",
      isExpiring: "",
      sortOrder: "DESC",
    })}`;

  const transactionsEndpoint = () => {
    return `transactions?${removeEmptyFilterQueries(
      qs.stringify({
        placeId: selectedPlaceId,
      }),
    )}`;
  };

  const sources = [
    {
      path: "/users",
      fetch: newest(
        apiConfig.users.list(
          qs.stringify(
            {
              filters: { $and: [{ role: { label: { $eq: "User" } } }] },
              pagination: { limit: 1, page: 1 },
              sort: "createdAt:desc",
            },
            { encode: false },
          ),
        ),
      ),
    },
    {
      path: "/bookings",
      fetch: newest(
        `booking/filter?${removeEmptyFilterQueries(
          qs.stringify(
            {
              placeId: selectedPlaceId,
              pagination: { page: 1, limit: 1 },
              sort: "createdAt:desc",
            },
            { encode: false },
          ),
        )}`,
      ),
    },
    // All drivers, newest first
    { path: "/drivers", fetch: newest(driversEndpoint()) },
    // Pending driver requests (same status your PendingDrivers page uses)
    { path: "/demandes", fetch: newest(driversEndpoint("in_review")) },
    {
      path: "/rides",
      fetch: newest(apiConfig.rides.all({ page: 1, includeHistory: "false" })),
    },
    { path: "/transactions", fetch: newest(transactionsEndpoint()) },
  ];

  const results = useQueries(
    sources.map((s) => ({
      queryKey: ["newDataAlert", s.path, selectedPlaceId],
      queryFn: s.fetch,
      enabled: Boolean(enabled && token),
      refetchInterval: 60_000,
    })),
  );

  const latestByPath: Record<string, string | null> = {};
  sources.forEach((s, i) => {
    latestByPath[s.path] = (results[i].data as string | null) ?? null;
  });

  const persist = (next: SeenMap) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSeen(next);
  };

  // First time we see a path: use the current newest item as the baseline,
  // so the badge doesn't light up for old data
  useEffect(() => {
    let next = seen;
    Object.entries(latestByPath).forEach(([path, latest]) => {
      if (latest && !next[path]) next = { ...next, [path]: latest };
    });
    if (next !== seen) persist(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(latestByPath)]);

  const markSeen = useCallback(
    (path: string) => {
      const latest = latestByPath[path];
      if (latest && seen[path] !== latest) persist({ ...seen, [path]: latest });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seen, JSON.stringify(latestByPath)],
  );

  const hasNew = (path: string) => {
    const latest = latestByPath[path];
    return Boolean(
      latest && seen[path] && new Date(latest) > new Date(seen[path]),
    );
  };

  return { hasNew, markSeen };
}

export function NewDataBadge() {
  return (
    <span
      className="ms-auto d-inline-flex align-items-center gap-1 rounded-pill flex-shrink-0"
      style={{
        padding: "8px 8px",
        fontSize: 10,
        fontWeight: 700,
        lineHeight: "14px",
        whiteSpace: "nowrap",
        color: "#FF7722",
        backgroundColor: "rgba(240, 129, 63, 0.12)",
        border: "1px solid rgba(240, 129, 63, 0.35)",
      }}
    >
      <span className="vz-pulse-dot" />
    </span>
  );
}
